<template>
    <branded-split-screen
        :heading="greeting"
        description="Sign in to keep your team shipping with confidence."
        :highlights="highlights"
    >
        <h2 class="text-2xl font-semibold text-taupe-900 dark:text-taupe-50">
            Sign in to your account
        </h2>
        <p
            v-if="trialLengthReady"
            class="mt-1 text-sm text-taupe-500 dark:text-taupe-400"
        >
            Or
            <router-link
                :to="{ name: 'RegisterScreen' }"
                class="font-medium text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                >start your {{ trialLength }}-day free trial!</router-link
            >
        </p>

        <div
            v-if="loggedOut"
            class="mt-6 p-4 border rounded-lg border-yellow-300 bg-yellow-100 font-medium text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-100"
        >
            We logged you out to keep your account secure. Log back in below.
        </div>

        <!-- Coming back from GitHub or Google without being signed in. -->
        <zen-message-block
            v-if="socialMessage?.type === 'error'"
            type="error"
            class="mt-6"
            data-testid="social-login-message"
        >
            {{ socialMessage.text }}
        </zen-message-block>
        <div
            v-else-if="socialMessage"
            role="status"
            class="mt-6 p-4 border rounded-lg border-yellow-300 bg-yellow-100 font-medium text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-100"
            data-testid="social-login-message"
        >
            {{ socialMessage.text }}
        </div>

        <form
            @submit.prevent="submit"
            @keydown.enter="submitFormOnEnter"
            class="mt-8 space-y-6"
        >
            <zen-message-block v-if="message" type="error">
                {{ message }}
            </zen-message-block>
            <div>
                <label
                    for="email"
                    class="block font-medium text-taupe-800 dark:text-taupe-100"
                >
                    Email address
                </label>
                <div class="mt-1">
                    <input
                        id="email"
                        name="email"
                        type="email"
                        autocomplete="email"
                        required
                        v-model="form.email"
                        class="appearance-none block w-full px-3 py-2 border border-taupe-300 rounded-md shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-hidden focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800 dark:text-taupe-50"
                    />
                </div>
            </div>

            <div>
                <label
                    for="password"
                    class="block font-medium text-taupe-800 dark:text-taupe-100"
                >
                    Password
                </label>
                <div class="mt-1">
                    <input
                        id="password"
                        name="password"
                        type="password"
                        autocomplete="current-password"
                        required
                        v-model="form.password"
                        class="appearance-none block w-full px-3 py-2 border border-taupe-300 rounded-md shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-hidden focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800 dark:text-taupe-50"
                    />
                </div>
            </div>

            <div class="flex items-center justify-between">
                <div>
                    <router-link
                        :to="{ name: 'ForgotPasswordScreen' }"
                        class="font-medium text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                        >Forgot your password?</router-link
                    >
                </div>
            </div>

            <div>
                <zen-button
                    type="submit"
                    variant="primary"
                    size="large"
                    full-width
                    :processing="loading"
                >
                    Sign In
                </zen-button>
            </div>
        </form>

        <social-login-buttons />
    </branded-split-screen>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BrandedSplitScreen from '@/screens/_components/BrandedSplitScreen.vue'
import SocialLoginButtons from '@/screens/_components/social/SocialLoginButtons.vue'
import { useAuthStore } from '@/stores/auth'
import { BRAND_HIGHLIGHTS } from '@/utils/brand-highlights'
import { submitFormOnEnter } from '@/utils/form-submit'
import { getRememberedFirstName } from '@/helpers/returning-user'
import { useFlagValue } from '@/composables/useFlagValue'
import {
    isSocialSessionOutcome,
    readSocialReturn,
    socialSignInMessage,
    withoutSocialReturn,
} from '@/helpers/social-login'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { value: trialLength, ready: trialLengthReady } = useFlagValue(
    'trial-length',
    30
)

const form = ref({
    email: '',
    password: '',
})
const loading = ref(false)
const message = ref('')
const loggedOut = ref(false)
const socialMessage = ref(null)
const highlights = BRAND_HIGHLIGHTS
const rememberedFirstName = ref(getRememberedFirstName())
const greeting = computed(() =>
    rememberedFirstName.value
        ? `Welcome back, ${rememberedFirstName.value}`
        : 'Welcome back'
)

const submit = async () => {
    loading.value = true
    message.value = ''

    try {
        await authStore.login(form.value)

        // Check for continue param first (for external redirects)
        // Validate to prevent open redirect - only allow same-origin URLs
        if (route.query.continue) {
            // A duplicated query param yields an array; use the first value.
            const continueParam = route.query.continue
            const continueUrl = Array.isArray(continueParam)
                ? continueParam[0]
                : continueParam

            // Resolve via the URL parser rather than a string-prefix check:
            // browsers normalize backslashes to slashes when navigating, so
            // e.g. "/\evil.com" passes a naive "starts with / but not //"
            // check yet still navigates off-origin. Comparing the parsed
            // origin catches that (and javascript:/data: schemes too).
            try {
                const target = new URL(
                    continueUrl,
                    globalThis.window.location.origin
                )
                if (target.origin === globalThis.window.location.origin) {
                    // Navigate with the already-validated URL object, not the
                    // raw string, so navigation can't parse the string
                    // differently than the validation above did.
                    globalThis.window.location.href = target.href
                    return
                }
            } catch {
                // Malformed continue param; fall through to default redirect.
            }
        }

        // Always go through the welcome/personalization screen first.
        // Any pending redirectAfterLogin is left in sessionStorage for
        // WelcomeScreen to pick up after the survey is done.
        await router.push({ name: 'WelcomeScreen' })
    } catch (error) {
        loading.value = false
        // Extract user-facing error message from error object
        message.value =
            error.response?.data?.message ||
            error.message ||
            'Something went wrong logging you in. Try again.'
    }
}

/**
 * The API ends a GitHub or Google sign-in by redirecting here with
 * `?social=<outcome>`. A successful one never gets this far: the router sends
 * the now signed-in person on to the welcome screen first. What does arrive is
 * a refusal to explain, or a "signed in" with no session behind it (the cookie
 * didn't stick), which is told the same way as any other failure.
 */
function showSocialReturn() {
    const socialReturn = readSocialReturn(route.query)
    if (socialReturn === null) {
        return
    }

    const { outcome, provider } = socialReturn
    socialMessage.value = socialSignInMessage(
        isSocialSessionOutcome(outcome) ? 'failed' : outcome,
        provider
    )

    // Drop the outcome from the address so a refresh doesn't replay it.
    router.replace({ query: withoutSocialReturn(route.query) }).catch(() => {
        // Ignore navigation duplication failures.
    })
}

onMounted(() => {
    if (route.query.t == '1') {
        loggedOut.value = true
    }

    showSocialReturn()
})
</script>
